// components/orbController.js — the single shared clock (module singleton) that
// drives every mounted Clark orb canvas in unison — the Clark-screen orbs AND the
// always-mounted sidebar nav orb. Every orb imports this same path, so there is
// exactly one clock/typing/speed state process-wide.
//
// Faithful to the old public/clark.js architecture (lines 18–130): ONE RAF loop,
// ONE phase clock (`t`), ONE eased speed/typing state shared across all orbs, so
// every orb on the page pulses and speeds up / slows down together. Orbs register
// their canvas context on mount and unregister on unmount; the RAF starts when the
// first orb registers and STOPS when the last unregisters (no leaked frames).
//
// drawOrb + the blob palette/math are copied verbatim from clark.js drawOrb/makeOrb.

const SPEED_REST = 0.0075;
const SPEED_TYPING = 0.022;
const SPEED_EXCITED = 0.065;

// The six metaballs — verbatim from clark.js makeOrb().
// B-PREMIUM P2 (item e): the orb THEMES — hues rotate from the navy base to the
// active theme's accent (mirrors seafoam's ClarkOrb; ink accents desaturate).
const BASE_HUE = 222;
let _themeHue = BASE_HUE;
let _themeSat = 1;
function _hexToHsl(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex).trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  const r = ((n >> 16) & 255) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b), d = max - min;
  let h = 0;
  if (d) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60; if (h < 0) h += 360;
  }
  const l = (max + min) / 2;
  const sat = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  return { h, s: sat, l };
}
function _refreshThemeHue() {
  try {
    const v = getComputedStyle(document.documentElement).getPropertyValue("--cc-accent-fill");
    const hsl = _hexToHsl(v);
    if (hsl) { const ink = hsl.s < 0.12; _themeHue = ink ? BASE_HUE : hsl.h; _themeSat = ink ? 0.18 : Math.max(0.35, Math.min(1, hsl.s * 1.15)); } // B-PREMIUM-2 P2: saturation FLOOR — the orb always reads as a real element
  } catch { /* keep last */ }
}
if (typeof document !== "undefined") {
  _refreshThemeHue();
  try { new MutationObserver(_refreshThemeHue).observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] }); } catch { /* ignore */ }
}
const BLOBS = [
  { ox: 0.28, oy: 0.32, sp: 0.65, sc: 0.58, h: 218, s: 88, l: 48 },
  { ox: 0.72, oy: 0.38, sp: 1.05, sc: 0.46, h: 195, s: 82, l: 52 },
  { ox: 0.38, oy: 0.68, sp: 0.88, sc: 0.52, h: 260, s: 72, l: 56 },
  { ox: 0.62, oy: 0.62, sp: 0.55, sc: 0.42, h: 178, s: 85, l: 50 },
  { ox: 0.5, oy: 0.48, sp: 1.25, sc: 0.36, h: 244, s: 68, l: 54 },
  { ox: 0.2, oy: 0.55, sp: 0.75, sc: 0.32, h: 205, s: 78, l: 58 },
];

// drawOrb — verbatim from clark.js:34–89. `speed` drives the typing factor exactly
// as the old code derived it from _orbSpeed.
function drawOrb(ctx, px, t, speed) {
  const typingFactor = Math.max(
    0,
    Math.min(1, (speed - SPEED_REST) / (SPEED_TYPING - SPEED_REST))
  );
  const lBump = typingFactor * 12;
  const orbitBoost = 1 + typingFactor * 0.12;
  const sheenAlpha = 0.22 + typingFactor * 0.15;

  ctx.clearRect(0, 0, px, px);
  ctx.save();
  ctx.beginPath();
  ctx.arc(px / 2, px / 2, px / 2 - 1, 0, Math.PI * 2);
  ctx.clip();
  const hueShift = _themeHue - BASE_HUE;
  const satOf = (v) => Math.round(v * _themeSat);
  const bg = ctx.createRadialGradient(px * 0.45, px * 0.42, 0, px / 2, px / 2, px / 2);
  bg.addColorStop(0, "hsl(" + (222 + hueShift) + "," + satOf(82) + "%,16%)");
  bg.addColorStop(1, "hsl(" + (232 + hueShift) + "," + satOf(92) + "%,9%)");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, px, px);
  BLOBS.forEach((b) => {
    const p = t * b.sp;
    const bx = px / 2 + Math.sin(p * 0.82 + b.ox * 6.28) * px * 0.34 * orbitBoost;
    const by = px / 2 + Math.cos(p * 0.91 + b.oy * 6.28) * px * 0.34 * orbitBoost;
    const br = px * (b.sc + Math.sin(p * 1.15) * 0.07);
    const hd = Math.sin(p * 0.48) * 22;
    const bl = b.l + lBump;
    const g = ctx.createRadialGradient(bx, by, 0, bx, by, br);
    const bh = b.h + hueShift;
    g.addColorStop(0, "hsla(" + (bh + hd) + "," + satOf(b.s) + "%," + bl + "%,.88)");
    g.addColorStop(0.5, "hsla(" + (bh + hd + 14) + "," + satOf(b.s - 4) + "%," + (bl - 4) + "%,.42)");
    g.addColorStop(1, "hsla(" + (bh + hd + 28) + "," + satOf(b.s - 10) + "%," + (bl - 12) + "%,0)");
    ctx.globalCompositeOperation = "screen";
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(bx, by, br, 0, Math.PI * 2);
    ctx.fill();
  });
  ctx.globalCompositeOperation = "source-over";
  const sheen = ctx.createRadialGradient(px * 0.38, px * 0.35, 0, px * 0.5, px * 0.5, px * 0.5);
  sheen.addColorStop(0, "rgba(255,255,255," + sheenAlpha + ")");
  sheen.addColorStop(0.35, "rgba(255,255,255,.05)");
  sheen.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = sheen;
  ctx.fillRect(0, 0, px, px);
  ctx.restore();
  ctx.beginPath();
  ctx.arc(px / 2, px / 2, px / 2 - 1.5, 0, Math.PI * 2);
  ctx.strokeStyle = "rgba(255,255,255,.18)";
  ctx.lineWidth = 1.5;
  ctx.stroke();
  const inner = ctx.createRadialGradient(px / 2, px / 2, px / 2 - 8, px / 2, px / 2, px / 2 - 2);
  inner.addColorStop(0, "rgba(255,255,255,0)");
  inner.addColorStop(1, "rgba(0,0,0,.22)");
  ctx.fillStyle = inner;
  ctx.beginPath();
  ctx.arc(px / 2, px / 2, px / 2 - 1.5, 0, Math.PI * 2);
  ctx.fill();
}

// ── Shared state (module singleton) ───────────────────────────────────────
const orbs = new Set(); // { ctx, px }
let rafId = null;
let t = 0;
let speed = SPEED_REST;
let isTyping = false;
let excitedUntil = 0;

// CLARK B2 prompt 7 — prefers-reduced-motion.
// The orb is continuous motion on every page that mounts one, which is exactly what
// this preference is about. Reduced motion does NOT blank it: the orb still draws, once,
// at a fixed phase, so the glyph and the avatar are still THERE — they just hold still.
// The RAF loop never starts, and pulseOrb becomes a no-op rather than a one-off jump.
let _reduced = false;
function _watchReducedMotion() {
  if (typeof window === "undefined" || !window.matchMedia) return;
  let mq;
  try { mq = window.matchMedia("(prefers-reduced-motion: reduce)"); } catch { return; }
  const apply = () => {
    _reduced = !!mq.matches;
    if (_reduced) {
      if (rafId != null) { cancelAnimationFrame(rafId); rafId = null; }
      // One static frame, so nothing is left blank or half-drawn.
      orbs.forEach((o) => drawOrb(o.ctx, o.px, t, SPEED_REST));
    } else if (rafId == null && orbs.size > 0) {
      rafId = requestAnimationFrame(loop);
    }
  };
  apply();
  if (mq.addEventListener) mq.addEventListener("change", apply);
  else if (mq.addListener) mq.addListener(apply);
}

function now() {
  return typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();
}

function loop() {
  // Exponential ease toward target speed — mirrors clark.js startOrbLoop().
  const excited = now() < excitedUntil;
  const target = excited ? SPEED_EXCITED : isTyping ? SPEED_TYPING : SPEED_REST;
  const lerpRate = excited ? 0.18 : isTyping ? 0.1 : 0.05;
  speed += (target - speed) * lerpRate;
  t += speed;
  orbs.forEach((o) => drawOrb(o.ctx, o.px, t, speed));
  rafId = requestAnimationFrame(loop);
}

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// 🔴 RUN AB — THE CROSS-BUNDLE BRIDGE. "ONE CLOCK PROCESS-WIDE" WAS ONLY EVER TRUE PER BUNDLE.
//
// The header of this file claims "exactly one clock/typing/speed state process-wide". homeOrb.js
// records the correction in its own header: that is true per BUNDLE, not per page. header.bundled.js
// carries this module and hydrates both the nav orb and the homepage widget orb; giving any OTHER
// island its own import of this file gives the page a SECOND clock, and then "the two orbs drift
// apart on screen and pulseOrb() from one would never reach the other."
//
// RUN AB needs an orb inside the capacity modal, and that modal lives in clark-widget.bundled.js —
// a DIFFERENT bundle from the header island. Without a bridge, clicking the modal's orb would move
// only itself: the homepage orb behind the scrim would keep drifting on its own clock, which is
// precisely the requirement ("every visible Clark orb on the page moves — including the one behind
// the modal") inverted.
//
// homeOrb.js's fix for the same problem was to hydrate FROM the header island. That works for a
// canvas that already exists in the static HTML; it cannot work for one a React component in
// another bundle mounts and unmounts on its own schedule. So the fix here is the general one:
// the FIRST copy of this module to load publishes its own state on `window`, and every later copy
// ADOPTS it instead of starting a rival clock. Whichever bundle loads first wins, and it does not
// matter which — there is exactly one clock per PAGE from then on.
//
// ⚠️ THE ADOPTION MUST COVER EVERY MUTATOR, NOT ONLY pulseOrb. An adopting copy that forwarded
// clicks but kept its own `orbs` Set and its own RAF would still draw its canvases from its own
// `t`, so the orbs would pulse together and drift apart — the drift is in the PHASE, not only in
// the excitement. registerOrb, setOrbTyping and pulseOrb all delegate.
// ═══════════════════════════════════════════════════════════════════════════════════════════════
const BRIDGE_KEY = "__ccOrbClock";
// The peer, if another bundle got here first. Read ONCE at module init: a later reassignment of
// window.__ccOrbClock must not silently re-point a clock that already has canvases registered.
const _peer = (typeof window !== "undefined" && window[BRIDGE_KEY]) || null;

// Register an orb canvas with the shared clock. Returns an unregister fn.
export function registerOrb(orb) {
  if (_peer) return _peer.registerOrb(orb);
  orbs.add(orb);
  if (_reduced) {
    // Draw once and stop. The orb is present and correct; it simply does not move.
    drawOrb(orb.ctx, orb.px, t, SPEED_REST);
    return () => { orbs.delete(orb); };
  }
  if (rafId == null) rafId = requestAnimationFrame(loop);
  return () => {
    orbs.delete(orb);
    if (orbs.size === 0 && rafId != null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
  };
}

// Global typing setter — every orb speeds up / slows down together. Wired to the
// in-flight flag (Clark is responding), matching the old _orbIsTyping trigger.
export function setOrbTyping(on) {
  if (_peer) return _peer.setOrbTyping(on);
  if (_reduced) return;
  isTyping = !!on;
}

// Brief excited burst — e.g. on orb click. Matches the old _orbExcitedUntil pulse.
export function pulseOrb(ms = 1200) {
  if (_peer) return _peer.pulseOrb(ms);
  if (_reduced) return; // a "brief excited burst" is precisely what reduce asks us not to do
  excitedUntil = now() + ms;
}

// Read the preference once the module's functions above are defined (the watcher calls
// drawOrb and loop), then keep tracking it live.
_watchReducedMotion();

// Test/diagnostic hook — reports whether the shared clock is currently animating.
export function _orbClockState() {
  if (_peer) return { ..._peer._orbClockState(), adopted: true };
  return { reduced: _reduced, running: rafId != null, orbs: orbs.size, adopted: false };
}

// 🔴 PUBLISH LAST, AND ONLY IF WE ARE THE FIRST. Publishing before the functions above are defined
// would expose a half-built object to a bundle that loads microseconds later; publishing when a
// peer already exists would let the second bundle steal the clock from the first, stranding every
// canvas the first one had already registered.
if (typeof window !== "undefined" && !_peer) {
  try {
    window[BRIDGE_KEY] = { registerOrb, setOrbTyping, pulseOrb, _orbClockState };
  } catch { /* a frozen or cross-origin window — this copy simply stays local */ }
}
